import { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { FileUpload, FileUploadHandlerEvent } from "primereact/fileupload";
import { Image } from "primereact/image";
import { Toast } from "primereact/toast";
import { ProfileField } from "../../api/types/crm/profile-fields";
import { useMutation } from "react-query";
import { createApiWithPath } from "../../api/client";
import { useGetImageUrl } from "../../api/mutations/field-values/useGetImageUrl";
import { useDeleteImage } from "../../api/mutations/field-values/useDeleteImage";

interface ImageFieldRendererProps {
  fieldType: ProfileField;
  value: any;
  onChange: (value: any) => void;
  readOnly?: boolean;
}

export default function ImageFieldRenderer({
  fieldType,
  value,
  onChange,
  readOnly,
}: ImageFieldRendererProps) {
  const toast = useRef<Toast>(null);
  const fileUploadRef = useRef<FileUpload>(null);
  const [uploading, setUploading] = useState(false);
  const [presignedUrl, setPresignedUrl] = useState<string | null>(null);

  // Value structure: { path: string, is_avatar: boolean }
  // Default is_avatar to true if field type is configured to allow avatar, false otherwise
  const defaultIsAvatar = fieldType.validation?.is_avatar_config ? true : false;
  const currentValue = value || { path: null, is_avatar: defaultIsAvatar };

  const getImageUrl = useGetImageUrl();
  const deleteImage = useDeleteImage();

  // Store mutate in ref to avoid dependency issues
  const getImageUrlMutateRef = useRef(getImageUrl.mutate);
  getImageUrlMutateRef.current = getImageUrl.mutate;

  // Fetch presigned URL when path changes
  useEffect(() => {
    if (currentValue.path) {
      getImageUrlMutateRef.current(currentValue.path, {
        onSuccess: (url) => setPresignedUrl(url),
      });
    } else {
      setPresignedUrl(null);
    }
  }, [currentValue.path]);

  const uploadMutation = useMutation(
    async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      const crmApi = createApiWithPath("crm");
      const response = await crmApi.post(
        `/field-values/${fieldType.id}/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return response.data.data;
    },
    {
      onSuccess: (imageValue) => {
        onChange(imageValue);
        if (imageValue.path) {
          getImageUrl.mutate(imageValue.path, {
            onSuccess: (url) => setPresignedUrl(url),
          });
        }
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Image uploaded successfully",
        });
        setUploading(false);
      },
      onError: (error: any) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error.response?.data?.message || "Upload failed",
        });
        setUploading(false);
        onChange(null);
        setPresignedUrl(null);
        // Reset FileUpload component
        fileUploadRef.current?.clear();
      },
      onSettled: () => {
        // Reset mutation state after completion
        uploadMutation.reset();
      },
    },
  );

  const onUpload = (event: FileUploadHandlerEvent) => {
    setUploading(true);
    uploadMutation.mutate(event.files[0]);
  };

  const onAvatarChange = (e: any) => {
    onChange({ ...currentValue, is_avatar: e.checked });
  };

  const onRemove = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (currentValue.path) {
      deleteImage.mutate(currentValue.path, {
        onSuccess: () => {
          onChange(null);
          setPresignedUrl(null);
          toast.current?.show({
            severity: "success",
            summary: "Success",
            detail: "Image deleted successfully",
          });
        },
        onError: () => {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: "Failed to delete image",
          });
        },
      });
    } else {
      onChange(null);
    }
  };

  if (readOnly) {
    if (!currentValue.path) return <span>No image</span>;
    if (getImageUrl.isLoading) return <span>Loading...</span>;
    return (
      <div className="flex flex-column gap-2">
        {presignedUrl && (
          <Image src={presignedUrl} alt={fieldType.label} width="150" preview />
        )}
        {currentValue.is_avatar && (
          <span className="text-sm text-gray-500">Avatar</span>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-column gap-3">
      <Toast ref={toast} />

      {currentValue.path ? (
        <div className="flex align-items-start gap-4">
          <div className="relative">
            {getImageUrl.isLoading ? (
              <div
                style={{
                  width: 200,
                  height: 150,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span>Loading...</span>
              </div>
            ) : presignedUrl ? (
              <Image
                src={presignedUrl}
                alt={fieldType.label}
                width="200"
                preview
              />
            ) : null}
            <Button
              icon={
                deleteImage.isLoading ? "pi pi-spin pi-spinner" : "pi pi-times"
              }
              className="p-button-rounded p-button-danger p-button-sm absolute"
              style={{ top: "-10px", right: "-10px" }}
              onClick={onRemove}
              disabled={deleteImage.isLoading}
              tooltip="Remove Image"
            />
          </div>

          {fieldType.validation?.is_avatar_config && (
            <div className="flex align-items-center">
              <Checkbox
                inputId={`avatar-${fieldType.id}`}
                checked={currentValue.is_avatar}
                onChange={onAvatarChange}
              />
              <label htmlFor={`avatar-${fieldType.id}`} className="ml-2">
                Set as Avatar
              </label>
            </div>
          )}
        </div>
      ) : (
        <FileUpload
          ref={fileUploadRef}
          mode="basic"
          name="file"
          accept="image/*"
          maxFileSize={
            fieldType.validation?.max_size
              ? fieldType.validation.max_size * 1024
              : 5000000
          }
          customUpload
          uploadHandler={onUpload}
          auto
          chooseLabel={uploading ? "Uploading..." : "Upload Image"}
          disabled={uploading}
          onValidationFail={(e) => {
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail:
                "File validation failed. Please check file size and type.",
            });
            setUploading(false);
            fileUploadRef.current?.clear();
          }}
        />
      )}
    </div>
  );
}
